import FlightOutlinedIcon from '@mui/icons-material/FlightOutlined'
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import { Avatar, Chip, Paper, Typography } from '@mui/material'
import type { ReactNode } from 'react'

import { Box } from '@/components/ui'
import {
  type ProductFlagTier,
  SELECTABLE_TIERS,
  TIER_LABELS,
  TIER_RANK,
} from '@/definitions/productFlags'

// Paper plane, aeroplane, rocket — the packages read as increasing lift.
const TIER_ICONS: Record<string, ReactNode> = {
  core: <SendOutlinedIcon fontSize='small' />,
  premium: <FlightOutlinedIcon fontSize='small' />,
  copilot: <RocketLaunchOutlinedIcon fontSize='small' />,
}

export interface TierSelectorProps {
  /** Null when the operator has not chosen a package and none is inferred. */
  selectedTier: ProductFlagTier | null
  /**
   * The package the practice is on according to its saved product flags, which is not
   * the selected one once the operator starts clicking around. Null when the practice
   * has nothing switched on, so no card is badged.
   */
  currentTier: ProductFlagTier | null
  /** How many product flags each package covers, for the caption under its name. */
  productFlagCounts: Record<ProductFlagTier, number>
  onSelect: (tier: ProductFlagTier) => void
}

/**
 * Chooses which package's product flags are open for editing.
 *
 * Manual switches have no button here: they belong to no package and stay editable
 * throughout. Selecting a package writes nothing on its own — it stages the product
 * flags above it off, which the operator then has to save.
 */
export const TierSelector = ({
  selectedTier,
  currentTier,
  productFlagCounts,
  onSelect,
}: TierSelectorProps) => (
  <Box variant='flexColumn' gap='md'>
    <Typography variant='h6' fontWeight={600}>
      Choose a plan
    </Typography>

    {SELECTABLE_TIERS.map((tier) => {
      const isSelected = tier === selectedTier
      // Strictly below the selection, never equal to it: the selected card has its
      // own treatment, and marking it as included too would leave two rules of
      // equal weight fighting over the icon colour.
      const isIncluded =
        selectedTier !== null && TIER_RANK[tier] < TIER_RANK[selectedTier]

      return (
        <Paper
          key={tier}
          variant='tierCard'
          role='button'
          aria-pressed={isSelected}
          data-included={isIncluded ? 'true' : 'false'}
          tabIndex={0}
          onClick={() => onSelect(tier)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onSelect(tier)
            }
          }}
        >
          <Box variant='flexBetween' gap='md'>
            <Box variant='flex' gap='md'>
              <Avatar variant='titleIcon'>{TIER_ICONS[tier]}</Avatar>
              <Box variant='flexColumn' gap='none' fullWidth={false}>
                <Typography variant='subtitle1' fontWeight={600}>
                  {TIER_LABELS[tier]}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {productFlagCounts[tier]} product flags
                </Typography>
              </Box>
            </Box>
            {/* Marks what the practice is actually on, which stops matching the
                highlighted card as soon as the operator selects a different one. */}
            {tier === currentTier && (
              <Chip variant='status' color='info' size='small' label='Current' />
            )}
          </Box>
        </Paper>
      )
    })}
  </Box>
)
